import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useColumnTranslations } from '../../hooks/useColumnTranslations';

export interface ColumnInfo {
  name: string;
  type: string;       // varchar, int4, int8, float8, numeric, date, bit, geometry, text, bool
  ordinal: number;
}

export interface EditFeatureDialogProps {
  open: boolean;
  title: string;
  columns: ColumnInfo[];
  geometry: any;            // GeoJSON geometry object
  tableName: string;
  onSave: (data: Record<string, any>) => void;
  onClose: () => void;
  saving?: boolean;
}

/** Map Postgres column types to HTML input types */
function inputType(colType: string): string {
  const t = colType.toLowerCase();
  if (t.includes('int') || t.includes('float') || t.includes('numeric') || t.includes('serial')) return 'number';
  if (t.includes('date')) return 'date';
  if (t.includes('bool') || t === 'bit') return 'checkbox';
  return 'text';
}

const HIDDEN_COLS = new Set(['gid', 'geom', 'the_geom', 'wkb_geometry', 'geometry', 'ogc_fid']);

export const EditFeatureDialog: React.FC<EditFeatureDialogProps> = ({
  open,
  title,
  columns,
  geometry,
  tableName,
  onSave,
  onClose,
  saving = false,
}) => {
  const { t: tCol } = useColumnTranslations();
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, any>>({});

  useEffect(() => {
    if (open && columns.length > 0) {
      const initial: Record<string, any> = {};
      columns.forEach((col) => {
        if (!HIDDEN_COLS.has(col.name.toLowerCase()) && col.type !== 'geometry') {
          initial[col.name] = '';
        }
      });
      setValues(initial);
    }
  }, [open, columns]);

  if (!open) return null;

  const editableCols = columns
    .filter((c) => !HIDDEN_COLS.has(c.name.toLowerCase()) && c.type !== 'geometry')
    .sort((a, b) => a.ordinal - b.ordinal);

  const handleChange = (colName: string, value: any) => {
    setValues((prev) => ({ ...prev, [colName]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(values);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Dialog */}
      <div
        className="relative rounded-lg shadow-2xl w-[420px] max-h-[80vh] flex flex-col overflow-hidden"
        style={{
          background: 'var(--gn-glass-bg)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--gn-glass-border)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gn">
          <h3 className="text-xs font-semibold text-gn-primary">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gn-danger-light text-gn-secondary hover:text-gn-danger transition-colors">
            <X size={14} />
          </button>
        </div>

        {/* Info bar */}
        <div className="px-4 py-2 border-b border-gn-light text-[10px] text-gn-secondary">
          {t('feature.table')}: <span className="font-medium text-gn-primary">{tableName}</span>
          &nbsp;·&nbsp;
          {t('feature.geometry_of', { type: geometry?.type || '?' })}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
          {editableCols.length === 0 && (
            <p className="text-xs text-gn-secondary italic">{t('feature.no_columns')}</p>
          )}

          {editableCols.map((col) => {
            const type = inputType(col.type);
            const isCheckbox = type === 'checkbox';

            return (
              <div key={col.name} className={cn(isCheckbox ? 'flex items-center gap-2' : '')}>
                <label className="block text-[11px] font-medium text-gn-secondary mb-0.5">
                  {tCol(col.name)}
                  <span className="ml-1 text-[9px] text-gn-secondary">({col.type})</span>
                </label>
                {isCheckbox ? (
                  <input
                    type="checkbox"
                    checked={!!values[col.name]}
                    onChange={(e) => handleChange(col.name, e.target.checked)}
                    className="rounded accent-blue-500"
                  />
                ) : (
                  <input
                    type={type}
                    value={values[col.name] ?? ''}
                    onChange={(e) => handleChange(col.name, e.target.value)}
                    className="w-full px-2 py-1.5 text-xs text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
                    step={type === 'number' ? 'any' : undefined}
                  />
                )}
              </div>
            );
          })}
        </form>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gn flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-gn-light text-gn-primary hover:bg-gn-tertiary transition-colors"
          >
            {t('buttons.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded font-medium transition-colors',
              saving
                ? 'bg-gn-tertiary text-gn-secondary cursor-not-allowed'
                : 'bg-gn-blue text-gn-inverse hover:bg-gn-blue',
            )}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {t('buttons.save')}
          </button>
        </div>
      </div>
    </div>
  );
};
